import axios from 'axios';

const COMFY_BASE = process.env.REACT_APP_COMFYUI_BASE || 'http://127.0.0.1:8000';
const DEFAULT_POLL_INTERVAL_MS = Number(process.env.REACT_APP_COMFYUI_POLL_INTERVAL_MS) || 1200;
const DEFAULT_POLL_TIMEOUT_MS = Number(process.env.REACT_APP_COMFYUI_POLL_TIMEOUT_MS) || 3_600_000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Upload an image blob to ComfyUI input folder. Returns saved filename.
 * @param {Blob} blob image blob
 * @param {string} filename desired filename
 */
export async function uploadImage(blob, filename = 'upload.png') {
  const attempt = async (url, fieldName) => {
    const form = new FormData();
    form.append(fieldName, blob, filename);
    form.append('subfolder', '');
    form.append('overwrite', 'true');

    const resp = await axios.post(url, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return resp?.data;
  };

  // Try /upload/image (official) then /upload fallback, field image then files
  const attempts = [
    { url: `${COMFY_BASE}/upload/image`, field: 'image' },
    { url: `${COMFY_BASE}/upload`, field: 'image' },
    { url: `${COMFY_BASE}/upload`, field: 'files' }
  ];

  let lastError;
  for (const a of attempts) {
    try {
      const data = await attempt(a.url, a.field);
      const saved = Array.isArray(data) ? data[0] : data;
      if (saved?.name) return saved.name;
      lastError = new Error('Unexpected upload response');
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error('ComfyUI upload failed');
}

/**
 * Run a ComfyUI workflow and return the first base64 image found.
 * @param {object} workflowJson full workflow
 * @param {object} overrides map of nodeId -> partial inputs to merge
 * @param {object} options { pollIntervalMs, pollTimeoutMs }
 */
export async function runWorkflow(workflowJson, overrides = {}, options = {}) {
  const pollIntervalMs = options.pollIntervalMs || DEFAULT_POLL_INTERVAL_MS;
  const pollTimeoutMs = options.pollTimeoutMs || DEFAULT_POLL_TIMEOUT_MS;

  // shallow clone so we don't mutate caller
  const payload = { ...workflowJson };
  Object.entries(overrides || {}).forEach(([nodeId, inputs]) => {
    if (!payload[nodeId]) payload[nodeId] = { inputs: {} };
    payload[nodeId] = {
      ...payload[nodeId],
      inputs: {
        ...(payload[nodeId].inputs || {}),
        ...(inputs || {}),
      },
    };
  });

  const promptResp = await axios.post(`${COMFY_BASE}/prompt`, { prompt: payload });
  const promptId = promptResp?.data?.prompt_id;
  if (!promptId) throw new Error('ComfyUI: no prompt_id returned');

  const start = Date.now();
  while (Date.now() - start < pollTimeoutMs) {
    const histResp = await axios.get(`${COMFY_BASE}/history/${promptId}`);
    const history = histResp?.data?.[promptId];
    const outputs = history?.outputs || {};

    const images = [];
    Object.values(outputs).forEach((out) => {
      (out.images || []).forEach((img) => images.push(img));
    });

    if (images.length > 0) {
      // Fetch the rendered image bytes and convert to base64 in-browser
      const { filename, subfolder, type } = images[0];
      const imgResp = await axios.get(`${COMFY_BASE}/view`, {
        params: { filename, subfolder, type },
        responseType: 'arraybuffer',
      });

      const base64 = arrayBufferToBase64(imgResp.data);
      const mime = 'image/png';
      return `data:${mime};base64,${base64}`;
    }

    await sleep(pollIntervalMs);
  }

  throw new Error('ComfyUI: timed out waiting for output');
}

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
